import React, { useState, useEffect } from 'react';
import { AppLayout } from '@/components/layout/AppLayout';
import { useProcurement } from '@/context/ProcurementContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  CheckCircle,
  Clock,
  XCircle,
  AlertTriangle,
  Building2,
  Calendar,
  DollarSign,
  FileText,
  Package,
  Shield,
  Phone,
  Mail,
  CheckSquare,
  AlertCircle,
  User,
  Truck,
  Receipt
} from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { DebitNotesForm } from '@/components/debitnotes';

const SecurityVerification = () => {
  const { state, actions } = useProcurement();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('pending-verification');
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDebitNotesForm, setShowDebitNotesForm] = useState(false);
  const [selectedPOForDebitNote, setSelectedPOForDebitNote] = useState<any>(null);
  const [verificationData, setVerificationData] = useState({
    productVerifications: [] as Array<{
      skuId: number;
      skuName: string;
      poQuantity: number;
      invoiceQuantity: number;
      receivedQuantity: number;
      variance: number;
    }>,
    gateEntryApproved: false,
    remarks: '',
    storeManagerContacted: false,
    rejectionReason: ''
  });

  useEffect(() => {
    if (state.purchaseOrders.length === 0) {
      actions.fetchPurchaseOrders();
    }
  }, []);

  // Filter POs by status
  const getPOsByStatus = (status: string) => {
    const statusMap: { [key: string]: string[] } = {
      'pending-verification': [],
      'rejected': ['REJECTED', 'FULLY_REJECTED'],
      'verified': ['FULLY_RECEIVED', 'PARTLY_RECEIVED']
    };

    const statuses = statusMap[status] || [];
    return state.purchaseOrders.filter(po => {
      let matchesStatus = statuses.includes(po.status);
      
      // For pending verification, only show POs that have been sent to vendors
      if (status === 'pending-verification') {
        matchesStatus = po.sentToVendor === true && po.status !== 'FULLY_RECEIVED' && po.status !== 'PARTLY_RECEIVED';
      }
      
      const vendorName = state.vendors.find(v => v.id === po.vendorId)?.name || '';
      const matchesSearch = 
        po.poNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vendorName.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesStatus && matchesSearch;
    }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  };

  // Calculate quantity verification for PO
  const getQuantityVerification = (po: any) => {
    const poQuantity = po.items.reduce((sum: number, item: any) => sum + item.quantity, 0);
    const totalInvoiceQuantity = verificationData.productVerifications.reduce((sum, pv) => sum + pv.invoiceQuantity, 0);
    const totalReceivedQuantity = verificationData.productVerifications.reduce((sum, pv) => sum + pv.receivedQuantity, 0);
    
    return {
      poQuantity,
      totalInvoiceQuantity,
      totalReceivedQuantity,
      variance: totalInvoiceQuantity - poQuantity
    };
  };

  const handleStartVerification = (po: any) => {
    setSelectedPO(po);
    
    // Initialize product-wise verification data
    const productVerifications = po.items.map((item: any) => {
      const sku = state.skus.find(s => s.id === item.skuId);
      return {
        skuId: item.skuId,
        skuName: sku?.name || `SKU-${item.skuId}`,
        poQuantity: item.quantity,
        invoiceQuantity: 0,
        receivedQuantity: 0,
        variance: 0
      };
    });
    
    setVerificationData({
      productVerifications,
      gateEntryApproved: false,
      remarks: '',
      storeManagerContacted: false,
      rejectionReason: ''
    });
    setShowVerificationModal(true);
  };

  const handleViewVerifiedPO = (po: any) => {
    setSelectedPO(po);
    setShowViewModal(true);
  };

  const handleVerifyPO = async () => {
    if (!selectedPO) return;

    try {
      if (verificationData.gateEntryApproved) {
        // Calculate total received quantity
        const totalReceivedQuantity = verificationData.productVerifications.reduce((sum, pv) => sum + pv.receivedQuantity, 0);
        const totalPOQuantity = selectedPO.items.reduce((sum: number, item: any) => sum + item.quantity, 0);
        
        // Determine PO status based on received quantity
        let newStatus = 'FULLY_RECEIVED';
        
        if (totalReceivedQuantity < totalPOQuantity) {
          newStatus = 'PARTLY_RECEIVED';
        } else if (totalReceivedQuantity === totalPOQuantity) {
          newStatus = 'FULLY_RECEIVED';
        }
        
        // Update PO with new status and product-wise verification data
        await actions.updatePurchaseOrder(selectedPO.id, { 
          ...selectedPO, 
          status: newStatus,
          verificationData: verificationData
        });
        
        const statusMessage = newStatus === 'FULLY_RECEIVED' 
          ? 'PO verified and fully received - marked as IQC Ready'
          : 'PO verified and partially received - marked as IQC Ready';
        
        toast.success(statusMessage);
      } else {
        // Reject PO
        await actions.updatePurchaseOrder(selectedPO.id, { 
          ...selectedPO, 
          status: 'REJECTED',
          rejectionReason: verificationData.rejectionReason,
          verificationData: verificationData
        });
        toast.error('PO rejected');
      }
      
      setShowVerificationModal(false);
    } catch (error) {
      console.error('Error verifying PO:', error);
      toast.error('Failed to verify PO');
    }
  };

  // Helper function to update product verification data
  const updateProductVerification = (skuId: number, field: 'invoiceQuantity' | 'receivedQuantity', value: number) => {
    setVerificationData(prev => ({
      ...prev,
      productVerifications: prev.productVerifications.map(pv => {
        if (pv.skuId === skuId) {
          const updated = { ...pv, [field]: value };
          
          // Auto-populate receivedQuantity when invoiceQuantity is changed
          if (field === 'invoiceQuantity') {
            // Only auto-populate if receivedQuantity is empty or 0
            if (!updated.receivedQuantity || updated.receivedQuantity === 0) {
              updated.receivedQuantity = value;
              // Show a subtle toast notification for user feedback
              toast.success(`Received Quantity auto-populated with ${value}`, {
                duration: 2000,
                position: 'top-right'
              });
            }
          }
          
          updated.variance = updated.invoiceQuantity - updated.poQuantity;
          return updated;
        }
        return pv;
      })
    }));
  };

  const handleContactStoreManager = () => {
    // Simulate contacting store manager
    toast.loading('Contacting store manager...', { id: 'contact-manager' });
    setTimeout(() => {
      toast.success('Store manager contacted successfully', { id: 'contact-manager' });
      setVerificationData(prev => ({ ...prev, storeManagerContacted: true }));
    }, 2000);
  };

  const handleRejectPO = () => {
    if (!selectedPO) return;
    
    // Send rejection notification to vendor
    toast.loading('Sending rejection notification to vendor...', { id: 'reject-vendor' });
    setTimeout(() => {
      toast.success('Vendor notified of rejection. Items ready for collection.', { id: 'reject-vendor' });
    }, 2000);
  };

  const handleCreateDebitNotes = (po: any) => {
    setSelectedPOForDebitNote(po);
    setShowDebitNotesForm(true);
  };

  const handleSaveDebitNote = async (debitNoteData: any): Promise<boolean> => {
    try {
      // Save to local storage
      const existingDebitNotes = JSON.parse(localStorage.getItem('DEBIT_NOTES') || '[]');
      const newDebitNote = {
        ...debitNoteData,
        id: Date.now(),
        createdAt: new Date().toISOString()
      };
      const updatedDebitNotes = [...existingDebitNotes, newDebitNote];
      localStorage.setItem('DEBIT_NOTES', JSON.stringify(updatedDebitNotes));

      toast.success('Debit note created successfully');
      setShowDebitNotesForm(false);
      setSelectedPOForDebitNote(null);
      return true;
    } catch (error) {
      console.error('Failed to create debit note:', error);
      toast.error('Failed to create debit note');
      return false;
    }
  };

  const handleCloseDebitNotesForm = () => {
    setShowDebitNotesForm(false);
    setSelectedPOForDebitNote(null);
  };

  const renderPOCard = (po: any, isVerified: boolean = false) => {
    const quantityVerification = getQuantityVerification(po);
    const isVerifiedStatus = po.status === 'FULLY_RECEIVED' || po.status === 'PARTLY_RECEIVED';
    
    return (
      <Card key={po.id} className="hover:shadow-md transition-shadow">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold">{po.poNumber}</h3>
                <Badge variant={getStatusVariant(po.status)}>
                  {po.status}
                </Badge>
                {po.sentToVendor && (
                  <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                    <Mail className="h-3 w-3 mr-1" />
                    Sent to Vendor
                  </Badge>
                )}
                {isVerifiedStatus && (
                  <Badge variant="default" className="text-green-600 border-green-200 bg-green-50">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Verified
                  </Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Vendor</p>
                    <p className="font-medium">{state.vendors.find(v => v.id === po.vendorId)?.name || 'Unknown Vendor'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">PO Number</p>
                    <p className="font-medium">{po.poNumber}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Amount</p>
                    <p className="font-medium">{formatCurrency(po.totalAmount)}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Created</p>
                    <p className="font-medium">
                      {new Date(po.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Show verification summary for verified POs */}
              {isVerifiedStatus && po.verificationData && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-green-800 mb-2">Security Verification Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-green-600 font-medium">Total PO Quantity</p>
                      <p className="text-lg font-bold text-green-800">
                        {po.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.poQuantity, 0) || 
                         po.items.reduce((sum: number, item: any) => sum + item.quantity, 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600 font-medium">Total Received</p>
                      <p className="text-lg font-bold text-green-800">
                        {po.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.receivedQuantity, 0) || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600 font-medium">Total Invoiced</p>
                      <p className="text-lg font-bold text-green-800">
                        {po.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.invoiceQuantity, 0) || 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-green-600 font-medium">Verification Date</p>
                      <p className="text-sm font-medium text-green-800">
                        {po.verificationData.verifiedAt ? new Date(po.verificationData.verifiedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {po.notes && (
                <div className="mt-2">
                  <p className="text-sm text-muted-foreground">Notes:</p>
                  <p className="text-sm">{po.notes}</p>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 ml-4">
              {isVerifiedStatus ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewVerifiedPO(po)}
                  className="flex items-center gap-2"
                >
                  <Eye className="h-4 w-4" />
                  View Details
                </Button>
              ) : (po.status === 'REJECTED' || po.status === 'FULLY_REJECTED') ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCreateDebitNotes(po)}
                  className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Receipt className="h-4 w-4" />
                  Debit Notes
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleStartVerification(po)}
                  className="flex items-center gap-2"
                >
                  <Shield className="h-4 w-4" />
                  Start Verification
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'CREATED':
      case 'APPROVED':
        return 'secondary';
      case 'FULLY_RECEIVED':
        return 'default';
      case 'REJECTED':
      case 'FULLY_REJECTED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const pendingPOs = getPOsByStatus('pending-verification');
  const rejectedPOs = getPOsByStatus('rejected');
  const verifiedPOs = getPOsByStatus('verified');

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Security Verification</h1>
            <p className="text-muted-foreground">Verify purchase orders and manage gate entry authorization</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        {/* Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by PO number, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                More Filters
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Verification</CardTitle>
              <Clock className="h-4 w-4 text-yellow-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{pendingPOs.length}</div>
              <p className="text-xs text-muted-foreground">
                POs sent to vendors awaiting security verification
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rejected</CardTitle>
              <XCircle className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{rejectedPOs.length}</div>
              <p className="text-xs text-muted-foreground">
                Rejected POs
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Verified</CardTitle>
              <Shield className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{verifiedPOs.length}</div>
              <p className="text-xs text-muted-foreground">
                Successfully verified
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Verification Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending-verification" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Pending ({pendingPOs.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Rejected ({rejectedPOs.length})
            </TabsTrigger>
            <TabsTrigger value="verified" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Verified ({verifiedPOs.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending-verification" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Pending Verification</h2>
              <p className="text-sm text-muted-foreground">
                {pendingPOs.length} POs awaiting security verification
              </p>
            </div>
            {pendingPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Clock className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No POs pending verification</h3>
                  <p className="text-muted-foreground text-center">
                    POs sent to vendors will appear here for security verification
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingPOs.map(po => renderPOCard(po, false))}
              </div>
            )}
          </TabsContent>


          <TabsContent value="rejected" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Rejected POs</h2>
              <p className="text-sm text-muted-foreground">
                {rejectedPOs.length} POs rejected by security
              </p>
            </div>
            {rejectedPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <XCircle className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No rejected POs</h3>
                  <p className="text-muted-foreground text-center">
                    Rejected POs will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {rejectedPOs.map(po => renderPOCard(po, false))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="verified" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Verified POs</h2>
              <p className="text-sm text-muted-foreground">
                {verifiedPOs.length} successfully verified POs
              </p>
            </div>
            {verifiedPOs.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No verified POs</h3>
                  <p className="text-muted-foreground text-center">
                    Verified POs will appear here
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {verifiedPOs.map(po => renderPOCard(po, true))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* View Verified PO Modal */}
        {showViewModal && selectedPO && (
          <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  Verified PO Details - {selectedPO.poNumber}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* PO Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">PO Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">PO Number</p>
                        <p className="font-medium">{selectedPO.poNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Vendor</p>
                        <p className="font-medium">{selectedPO.vendorName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <p className="font-medium">{selectedPO.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Amount</p>
                        <p className="font-medium">{formatCurrency(selectedPO.totalAmount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Verification Summary */}
                {selectedPO.verificationData && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        Security Verification Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-6">
                        {/* Summary Row */}
                        <div className="grid grid-cols-4 gap-4 p-4 bg-green-50 rounded-lg">
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-semibold mb-2">Total PO Quantity</p>
                            <p className="text-2xl font-bold text-green-800">
                              {selectedPO.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.poQuantity, 0) || 
                               selectedPO.items.reduce((sum: number, item: any) => sum + item.quantity, 0)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-semibold mb-2">Total Received</p>
                            <p className="text-2xl font-bold text-green-800">
                              {selectedPO.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.receivedQuantity, 0) || 0}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-semibold mb-2">Total Invoiced</p>
                            <p className="text-2xl font-bold text-green-800">
                              {selectedPO.verificationData.productVerifications?.reduce((sum: number, pv: any) => sum + pv.invoiceQuantity, 0) || 0}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-sm text-green-600 font-semibold mb-2">Verification Date</p>
                            <p className="text-lg font-bold text-green-800">
                              {selectedPO.verificationData.verifiedAt ? new Date(selectedPO.verificationData.verifiedAt).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Individual Product Verification Details */}
                        {selectedPO.verificationData.productVerifications && selectedPO.verificationData.productVerifications.length > 0 && (
                          <div className="space-y-4">
                            <h4 className="text-md font-semibold text-gray-700">Product-wise Verification Details</h4>
                            {selectedPO.verificationData.productVerifications.map((product: any, index: number) => (
                              <div key={product.skuId} className="border rounded-lg p-4 bg-white">
                                <div className="flex items-center justify-between mb-4">
                                  <div>
                                    <h5 className="font-semibold text-gray-800">{product.skuName}</h5>
                                    <p className="text-sm text-gray-500">SKU ID: {product.skuId}</p>
                                  </div>
                                  {product.variance !== 0 && (
                                    <Badge variant={product.variance > 0 ? "default" : "destructive"}>
                                      {product.variance > 0 ? '+' : ''}{product.variance} variance
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="grid grid-cols-3 gap-4">
                                  <div className="text-center p-4 rounded-lg border-2 border-blue-300 bg-blue-50">
                                    <p className="text-sm text-blue-700 font-semibold mb-2">PO Quantity</p>
                                    <p className="text-2xl font-bold text-blue-900">{product.poQuantity}</p>
                                  </div>
                                  <div className="text-center p-4 rounded-lg border-2 border-green-300 bg-green-50">
                                    <p className="text-sm text-green-700 font-semibold mb-2">Received Quantity</p>
                                    <p className="text-2xl font-bold text-green-900">{product.receivedQuantity}</p>
                                  </div>
                                  <div className="text-center p-4 rounded-lg border-2 border-purple-300 bg-purple-50">
                                    <p className="text-sm text-purple-700 font-semibold mb-2">Invoiced Quantity</p>
                                    <p className="text-2xl font-bold text-purple-900">{product.invoiceQuantity}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Verification Notes */}
                        {selectedPO.verificationData.remarks && (
                          <div>
                            <h4 className="text-md font-semibold text-gray-700 mb-2">Verification Remarks</h4>
                            <div className="p-3 bg-gray-50 rounded-lg">
                              <p className="text-sm text-gray-800">{selectedPO.verificationData.remarks}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setShowViewModal(false)}
                >
                  Close
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Security Verification Modal */}
        {showVerificationModal && selectedPO && (
          <Dialog open={showVerificationModal} onOpenChange={setShowVerificationModal}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Security Verification - {selectedPO.poNumber}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6">
                {/* PO Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">PO Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">PO Number</p>
                        <p className="font-medium">{selectedPO.poNumber}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Vendor</p>
                        <p className="font-medium">{selectedPO.vendorName}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Status</p>
                        <p className="font-medium">{selectedPO.status}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Amount</p>
                        <p className="font-medium">{formatCurrency(selectedPO.totalAmount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quantity Verification */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      1. Product-wise Quantity Verification
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Summary Row */}
                      <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="text-center">
                          <p className="text-sm text-gray-600 font-semibold mb-2">Total PO Quantity</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {verificationData.productVerifications.reduce((sum, pv) => sum + pv.poQuantity, 0)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 font-semibold mb-2">Total Invoice Quantity</p>
                          <p className="text-2xl font-bold text-purple-600">
                            {verificationData.productVerifications.reduce((sum, pv) => sum + pv.invoiceQuantity, 0)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 font-semibold mb-2">Total Received Quantity</p>
                          <p className="text-2xl font-bold text-green-600">
                            {verificationData.productVerifications.reduce((sum, pv) => sum + pv.receivedQuantity, 0)}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-sm text-gray-600 font-semibold mb-2">Overall Variance</p>
                          <p className={`text-2xl font-bold ${
                            verificationData.productVerifications.reduce((sum, pv) => sum + pv.variance, 0) === 0 
                              ? 'text-green-600' 
                              : verificationData.productVerifications.reduce((sum, pv) => sum + pv.variance, 0) > 0 
                                ? 'text-orange-600' 
                                : 'text-red-600'
                          }`}>
                            {verificationData.productVerifications.reduce((sum, pv) => sum + pv.variance, 0) > 0 ? '+' : ''}
                            {verificationData.productVerifications.reduce((sum, pv) => sum + pv.variance, 0)}
                          </p>
                        </div>
                      </div>

                      {/* Individual Product Verification */}
                      <div className="space-y-4">
                        <h4 className="text-md font-semibold text-gray-700">Individual Product Verification</h4>
                        {verificationData.productVerifications.map((product, index) => (
                          <div key={product.skuId} className="border rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-4">
                              <div>
                                <h5 className="font-semibold text-gray-800">{product.skuName}</h5>
                                <p className="text-sm text-gray-500">SKU ID: {product.skuId}</p>
                              </div>
                              {product.variance !== 0 && (
                                <Badge variant={product.variance > 0 ? "default" : "destructive"}>
                                  {product.variance > 0 ? '+' : ''}{product.variance} variance
                                </Badge>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-3 gap-4">
                              <div className="text-center p-4 rounded-lg border-2 border-blue-300 bg-blue-50">
                                <p className="text-sm text-blue-700 font-semibold mb-2">PO Quantity</p>
                                <Input
                                  type="number"
                                  value={product.poQuantity}
                                  className="text-center text-lg font-bold text-blue-900 border-2 border-blue-200 bg-blue-100"
                                  disabled
                                />
                              </div>
                              <div className="text-center p-4 rounded-lg border-2 border-purple-300 bg-purple-50">
                                <p className="text-sm text-purple-700 font-semibold mb-2">Invoice Quantity</p>
                                <Input
                                  type="number"
                                  value={product.invoiceQuantity || ''}
                                  onChange={(e) => updateProductVerification(product.skuId, 'invoiceQuantity', parseInt(e.target.value) || 0)}
                                  className="text-center text-lg font-bold text-purple-900 border-2 border-purple-200 bg-purple-100 focus:border-purple-400 focus:ring-2 focus:ring-purple-200"
                                />
                              </div>
                              <div className="text-center p-4 rounded-lg border-2 border-green-300 bg-green-50">
                                <p className="text-sm text-green-700 font-semibold mb-2">Received Quantity</p>
                                <Input
                                  type="number"
                                  value={product.receivedQuantity || ''}
                                  onChange={(e) => updateProductVerification(product.skuId, 'receivedQuantity', parseInt(e.target.value) || 0)}
                                  className="text-center text-lg font-bold text-green-900 border-2 border-green-200 bg-green-100 focus:border-green-400 focus:ring-2 focus:ring-green-200"
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      {/* Overall Variance Alert */}
                      {verificationData.productVerifications.some(pv => pv.variance !== 0) && (
                        <Alert className="border-orange-200 bg-orange-50">
                          <AlertTriangle className="h-4 w-4 text-orange-600" />
                          <AlertDescription className="text-orange-800">
                            <strong>Variance Detected:</strong> Some products have quantity variances. Please review individual product quantities above.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Gate Entry Authority */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Truck className="h-5 w-5" />
                      2. Gate Entry Authority
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Authorize gate entry and mark PO as IQC Ready for quality inspection.
                      </p>
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="gate-entry-approved"
                          checked={verificationData.gateEntryApproved}
                          onCheckedChange={(checked) => 
                            setVerificationData(prev => ({ ...prev, gateEntryApproved: !!checked }))
                          }
                        />
                        <Label htmlFor="gate-entry-approved" className="text-sm font-medium">
                          Approve gate entry and mark as IQC Ready
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Store Manager Contact */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Phone className="h-5 w-5" />
                      3. Store Manager Contact
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        Contact store manager if there are any issues or discrepancies.
                      </p>
                      <div className="flex items-center gap-4">
                        <Button
                          variant="outline"
                          onClick={handleContactStoreManager}
                          disabled={verificationData.storeManagerContacted}
                          className="flex items-center gap-2"
                        >
                          <Phone className="h-4 w-4" />
                          Contact Store Manager
                        </Button>
                        {verificationData.storeManagerContacted && (
                          <Badge variant="default" className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Contacted
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Remarks */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertCircle className="h-5 w-5" />
                      Remarks & Notes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="remarks">Verification Remarks</Label>
                        <Textarea
                          id="remarks"
                          placeholder="Enter any remarks or observations..."
                          value={verificationData.remarks}
                          onChange={(e) => setVerificationData(prev => ({ ...prev, remarks: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="rejection-reason">Rejection Reason (if applicable)</Label>
                        <Textarea
                          id="rejection-reason"
                          placeholder="Enter reason for rejection..."
                          value={verificationData.rejectionReason}
                          onChange={(e) => setVerificationData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="destructive"
                      onClick={() => {
                        handleRejectPO();
                        setShowVerificationModal(false);
                      }}
                      className="flex items-center gap-2"
                    >
                      <XCircle className="h-4 w-4" />
                      Reject PO
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setShowVerificationModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleVerifyPO}
                      disabled={!verificationData.gateEntryApproved}
                      className="flex items-center gap-2"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Verify & Approve
                    </Button>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Debit Notes Form Modal */}
        {showDebitNotesForm && selectedPOForDebitNote && (
          <DebitNotesForm
            isOpen={showDebitNotesForm}
            onClose={handleCloseDebitNotesForm}
            onSave={handleSaveDebitNote}
            linkedPO={selectedPOForDebitNote}
            mode="po"
          />
        )}
      </div>
    </AppLayout>
  );
};

export default SecurityVerification;